import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Ticket } from '@app/modules/model/ticket';
import { TicketInvoice } from '@app/modules/model/ticketinvoice';

import { toastService } from '@app/modules/common/toast';
import { alertService } from '@app/modules/common/alert';
import { languageSupport, languageService } from '@app/modules/common/language';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';
import { Business } from '@app/modules/model/business';
import { UiBusinessEditChange } from '@app/components/model/client/ui-edit/ui-edit';

@Component({
    selector: 'ui-ticket-invoice',
    templateUrl:'./ui-invoice.html',
    styleUrls: [ 
        './ui-invoice.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiInvoiceEdit extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('ticket') _ticket: Ticket = null;

    get place(){
        return this._ticket.place;
    }

    private _invoicing = false;
    private _business = null;
    get business(){
        if (this._business == null){
            this._business = new Business(null, this.data);
            if (this._ticket.invoice){
                this._business.name = this._ticket.invoice.client.name;
                this._business.taxid = this._ticket.invoice.client.taxid;
                this._business.email = this._ticket.invoice.client.email;
                this._business.address = this._ticket.invoice.client.address;
                this._business.postalcode = this._ticket.invoice.client.postalcode;
                this._business.region = this._ticket.invoice.client.region;
            }
        }

        return this._business;
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _ticket_subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private toast: toastService, private alertCtrl: alertService, private data: dataService, public view: viewService){
        super(lang, change);
    }

    private _invoiceProceedable = false;
    private _taxIdUnverified = false;

    ngOnInit(){
        // nothing to do
    }

    ngOnChanges() {
        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        this._ticket_subscription = this._ticket.OnRefresh.subscribe(
        data => {
            // Re-derive the business from the (possibly updated) ticket.
            // Do NOT reset _invoiceProceedable / _taxIdUnverified here:
            // ui-business-edit emits onChange on its own ngOnChanges, but
            // there's a race where the ticket OnRefresh fires after that
            // initial emit, leaving the button disabled with valid data.
            this._business = null;
            this.change.markForCheck();
        });
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }
    }
    
    @Output('onCompleted') _onCompleted = new EventEmitter<any>();    
    OnCompleted(done){
        this._onCompleted.next(done);
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    OnBussinessChange(ev: UiBusinessEditChange){
        this._invoiceProceedable = ev.invoiceProceedable;
        this._taxIdUnverified = ev.taxIdUnverified;
        this.change.markForCheck();
    }

    get CanTicketInvoice(){
        // Drop the legacy _isdirty gate: it required the user to touch the
        // form even when prefilled fiscal data was already valid, leaving
        // the button disabled. Validity comes from _invoiceProceedable.
        return this._invoiceProceedable;
    }

    private _CreateBusiness(){
        let _business: Business = null;

        let _merchantname = this.business.name;
        let _merchanttaxid = this.business.taxid;
        let _merchantemail = this.business.email;
        let _merchantaddress = this.business.address;

        for (let _client of this._ticket.place.clients){
            if (_client.IsValid && (_client.name == _merchantname) && (_client.taxid == _merchanttaxid)){
                _business = _client;
            }      
        }
        
        // create new business on address change
        if (_business && (_business.address != _merchantaddress)){
            _business.status = 'DE';
            _business.DoCommit();       
            _business = null;
        }

        // if business not found: use the created one
        if (!_business) {
            _business = this.business;
            if (_business){
                _business.place = this._ticket.place;
                _business.name = _merchantname;
                _business.taxid = _merchanttaxid;
                _business.email = _merchantemail;
                _business.address = _merchantaddress;
                _business.status = 'AC';
            }
        }
        // otherwise update the business information
        else {
            _business.name = _merchantname;
            _business.taxid = _merchanttaxid;
            _business.email = _merchantemail;
        }
        
        return _business;
    }

    async OnTicketInvoice(){
        if (this._invoicing){
            return;
        }
        this._invoicing = true;

        if (this._taxIdUnverified){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_ticket_invoice_unsafe_taxid_title'),
                message: this.lang.tr('@place_ticket_invoice_unsafe_taxid_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]
            });

            alert.onDidDismiss().then((detail: any) => {
                if (detail.data === true){
                    this._DoTicketInvoice();
                }
                else {
                    this._invoicing = false;
                }
            });

            await alert.present();
        }
        else {
            this._DoTicketInvoice();
        }
    }

    private _DoTicketInvoice(){
        this._ticket.DoPrint(false, false, true);

        let _invoice = new TicketInvoice(null, this.data, this._ticket.ObjOptions);
        if (_invoice){
            _invoice.ticket = this._ticket;
            _invoice.client = this._CreateBusiness();
            _invoice.status = 'AC';
        }

        this._ticket.invoice = _invoice;
        if (this._ticket.pendpay){
            this._ticket.payment = this._ticket.pendpay;
        }

        this._ticket.DoCommit().then(
        data => {
            if (this._ticket.pendpay){
                this.toast.ShowAlert('primary', this.lang.tr('@place_ticket_sent_toast'), 2000);
            }
        });
        this.OnCompleted(true);
    }
    
    OnTicketCancel(){
        if (this._ticket.pendpay){
            this._ticket.pendpay = null;
        }

        this.OnCompleted(false);
    }
}
